import { Play, Edit, Trash2, Copy, Check, Power, PowerOff, Zap, Link2, Globe, FileText, History, BookOpen, User } from 'lucide-react';
import type { SavedQuery } from '../../types/query';
import { AUTOMATION_INTERVALS } from '../../types/query';
import { queryService } from '../../services/queryService';
import { documentationService } from '../../services/documentationService';
import { useState } from 'react';

interface QueryCardProps {
  query: SavedQuery;
  onEdit: (query: SavedQuery) => void;
  onDelete: (query: SavedQuery) => void;
  onExecute: (query: SavedQuery) => void;
  onToggleActive: (query: SavedQuery) => void;
  onShowHistory: (query: SavedQuery) => void;
  onShowSwagger: (query: SavedQuery) => void;
}

export default function QueryCard({ query, onEdit, onDelete, onExecute, onToggleActive, onShowHistory, onShowSwagger }: QueryCardProps) {
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const endpointUrl = queryService.generateEndpointUrl(query.endpoint_slug);

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(endpointUrl);
      setCopiedEndpoint(true);
      setTimeout(() => setCopiedEndpoint(false), 2000);
    } catch (error) {
      console.error('Failed to copy endpoint:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIntervalLabel = (interval: string | null) => {
    const found = AUTOMATION_INTERVALS.find(i => i.value === interval);
    return found ? found.label : interval;
  };

  const handleExportDocumentation = () => {
    documentationService.generatePDF(query);
  };

  return (
    <div className={`bg-white rounded-lg border shadow-sm transition-all hover:shadow-md ${
      query.is_active ? 'border-gray-200' : 'border-gray-300 opacity-75'
    }`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{query.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                query.is_active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {query.is_active ? 'Ativo' : 'Inativo'}
              </span>
              {query.automation_enabled && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  <Zap className="w-3 h-3" />
                  Automação
                </span>
              )}
              {(query.availability_type === 'api' || query.availability_type === 'both') && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  <Globe className="w-3 h-3" />
                  API
                </span>
              )}
              {(query.availability_type === 'integration' || query.availability_type === 'both') && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                  <Link2 className="w-3 h-3" />
                  {[query.integration_solace_enabled && 'Solace', query.integration_sap_btp_enabled && 'SAP BTP'].filter(Boolean).join(' + ') || 'Integração'}
                </span>
              )}
            </div>
            {query.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{query.description}</p>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-3 mb-4">
          <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
            {query.sql_query}
          </pre>
        </div>

        <div className="space-y-2 mb-4">
          {(query.availability_type === 'api' || query.availability_type === 'both' || !query.availability_type) && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Endpoint:</span>
              <code className="flex-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded truncate">
                {endpointUrl}
              </code>
              <button
                onClick={handleCopyEndpoint}
                className={`p-1 rounded transition-colors ${
                  copiedEndpoint ? 'text-green-600' : 'text-gray-400 hover:text-blue-600'
                }`}
                title="Copiar endpoint"
              >
                {copiedEndpoint ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          )}

          {query.selected_columns.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Colunas:</span>
              {query.selected_columns.map((col) => (
                <span key={col} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                  {col}
                </span>
              ))}
            </div>
          )}

          {query.automation_interval && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">Frequência:</span>
                <span className="text-xs text-blue-800">{getIntervalLabel(query.automation_interval)}</span>
              </div>
              {query.automation_last_run && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">Última execução:</span>
                  <span className="text-xs text-blue-800">{formatDate(query.automation_last_run)}</span>
                </div>
              )}
            </div>
          )}

          {(query.integration_solace_enabled || query.integration_sap_btp_enabled) && (
            <div className="mt-3 p-3 bg-orange-50 rounded-lg space-y-3">
              {query.integration_solace_enabled && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-orange-800">Solace Event Broker</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-orange-700">Queue:</span>
                    <code className="text-xs text-orange-800">{query.integration_solace_queue_name}</code>
                  </div>
                </div>
              )}
              {query.integration_sap_btp_enabled && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-blue-800">SAP BTP</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-700">API:</span>
                    <code className="text-xs text-blue-800 truncate" title={query.integration_sap_btp_api_path || ''}>
                      {query.integration_sap_btp_api_path}
                    </code>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
              <User className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-shrink-0">Criado por</span>
              <span className="font-medium text-gray-700 truncate" title={query.created_by_name || undefined}>
                {query.created_by_name || 'Usuário não identificado'}
              </span>
              {query.created_by_email && (
                <span className="text-gray-400 truncate" title={query.created_by_email}>
                  ({query.created_by_email})
                </span>
              )}
            </div>
            <span className="block text-xs text-gray-400">
              Atualizado em {formatDate(query.updated_at)}
            </span>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onToggleActive(query)}
              className={`p-2 rounded-lg transition-colors ${
                query.is_active
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
              title={query.is_active ? 'Desativar serviço' : 'Ativar serviço'}
            >
              {query.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
            </button>
            <button
              onClick={() => onShowHistory(query)}
              className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
              title="Ver histórico de execuções"
            >
              <History className="w-4 h-4" />
            </button>
            <button
              onClick={handleExportDocumentation}
              className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              title="Exportar documentação"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={() => onShowSwagger(query)}
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
              title="Swagger / Testar API"
            >
              <BookOpen className="w-4 h-4" />
            </button>
            <button
              onClick={() => onExecute(query)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Executar serviço"
            >
              <Play className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(query)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Editar serviço"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(query)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Excluir serviço"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
